/**
 * Shared types for the integration platform.
 *
 * Three layers:
 *
 *   1. `ObservedContext` — what an `IntegrationProvider` extracts from the
 *      current EMR page in the user's browser. Wire-safe, sent to the
 *      service-gateway for patient matching.
 *
 *   2. `MatchState` — the gateway's response, modeling the full PRD §6
 *      patient-match state machine (matched, likely, multiple, none,
 *      unsafe, error, loading).
 *
 *   3. `RealTimeModeData` — the actual content rendered in the sidebar
 *      when a match resolves: balance, claims, issues, recommended actions.
 *
 * The set of supported integration providers is enumerated by
 * `IntegrationProviderId` — when adding e.g. Epic / Athena, extend the
 * union and ship a runtime provider in `clients/extension/src/integrations/`.
 */

import { z } from 'zod';

// ─── Integration provider identity ────────────────────────────────────────

/**
 * Canonical ID of every supported EMR / practice management integration.
 * Used as a discriminator on `ObservedContext`, persisted on chat messages
 * that carry integration context, and logged for audit (PRD §10).
 */
export const IntegrationProviderIdSchema = z.enum(['modmed', 'ecw', 'centralreach']);
export type IntegrationProviderId = z.infer<typeof IntegrationProviderIdSchema>;

/**
 * Lightweight metadata describing an integration. The runtime DOM-extracting
 * provider lives in the extension; this shape is everything the web/server
 * sides need to know.
 */
export const IntegrationMetadataSchema = z.object({
  id: IntegrationProviderIdSchema,
  /** Display name shown in the chat composer's attach-context affordance. */
  name: z.string(),
  /** Short description for UX surfaces. */
  description: z.string(),
  /**
   * Whether this integration is enabled for the current build / org.
   * `false` hides it from pickers but does NOT prevent the bridge from
   * loading — privacy gating happens at the URL allowlist layer.
   */
  enabled: z.boolean(),
});
export type IntegrationMetadata = z.infer<typeof IntegrationMetadataSchema>;

// ─── Observed context — what the bridge extracts ──────────────────────────

/**
 * EMR page types we care about. Drives `page_type` in PRD §7 requests and
 * gates which sidebar content / chat-context attachments are valid.
 *
 * `unknown` is the catch-all for supported origins on pages we don't yet
 * have an extraction strategy for — the bridge can still report presence
 * for audit/observability without exposing financial data.
 */
export const EmrPageTypeSchema = z.enum([
  'patient_account',
  'patient_chart',
  'scheduling',
  'billing',
  'claims',
  'unknown',
]);
export type EmrPageType = z.infer<typeof EmrPageTypeSchema>;

/**
 * Raw fields extracted from the EMR DOM. Every field is optional — the
 * bridge must NEVER fabricate. Match resolution downstream decides what is
 * usable.
 */
/** One EMR-rendered aging bucket, e.g. `{ label: '0-30', amount: 125.5 }`. */
export const AgingBucketSchema = z.object({
  /** Bucket label exactly as the EMR renders it ('0-30', '120+', …). */
  label: z.string(),
  amount: z.number(),
});
export type AgingBucket = z.infer<typeof AgingBucketSchema>;

/** Patient A/R aging: the ordered buckets plus the EMR's own total. */
export const PatientAgingSchema = z.object({
  /** Ordered oldest-last, exactly as rendered. */
  buckets: z.array(AgingBucketSchema),
  total: z.number().optional(),
});
export type PatientAging = z.infer<typeof PatientAgingSchema>;

export const ObservedFieldsSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  /** ISO 8601 (YYYY-MM-DD) — bridge normalizes locale-specific date strings. */
  dob: z.string().optional(),
  /** Medical Record Number — most reliable matching key when present. */
  mrn: z.string().optional(),
  /** EMR-internal account identifier (PMS ID / pid / etc.). */
  emrAccountId: z.string().optional(),
  /** EMR-internal patient identifier as seen in the URL/router. */
  emrPatientId: z.string().optional(),
  /**
   * Patient balance as currently rendered in the EMR UI (dollars,
   * decimal). Powers the V1.1 discrepancy badge: when present, the
   * resolver compares it to Enter's authoritative balance and surfaces
   * a "numbers disagree" chip on the Outstanding Balance card.
   *
   * Optional — bridge MUST omit when not extractable rather than
   * guessing.
   */
  emrVisibleBalance: z.number().optional(),
  /**
   * Patient A/R aging as rendered by the EMR, when it exposes one.
   *
   * Bucket LABELS are passed through verbatim rather than mapped onto fixed
   * fields, because each EMR slices aging differently (CentralReach:
   * 0-30/31-60/61-90/91-120/120+; others use 90+). The UI renders whatever
   * labels arrive, so a new EMR needs no schema change.
   *
   * Patient-responsibility only — never payer/insurance aging.
   */
  emrPatientAging: PatientAgingSchema.optional(),
});
export type ObservedFields = z.infer<typeof ObservedFieldsSchema>;

/**
 * Full bridge payload sent to `POST /real-time-mode/resolve` and used as
 * the structured chat attachment when the user attaches ModMed context to
 * a chat message.
 */
export const ObservedContextSchema = z.object({
  /** Provider that produced this context. */
  provider: IntegrationProviderIdSchema,
  /** EMR build identifier we observed (sandbox vs prod vs staging). */
  origin: z.string(),
  /** Full page URL at extraction time. */
  pageUrl: z.string(),
  /** Page classification (see EmrPageType). */
  pageType: EmrPageTypeSchema,
  /** Patient/account fields extracted from the page. */
  observedFields: ObservedFieldsSchema,
  /** Bridge extraction timestamp (epoch ms). */
  observedAt: z.number(),
});
export type ObservedContext = z.infer<typeof ObservedContextSchema>;

// ─── Match state machine — PRD §6 ─────────────────────────────────────────

/**
 * Which fields the backend used to compute the match — surfaced in the
 * "Matched Patient" card's `Basis` line and persisted for audit.
 */
export const MatchBasisSchema = z.enum([
  'mrn',
  'emr_account_id',
  'emr_patient_id',
  'dob',
  'name',
]);
export type MatchBasis = z.infer<typeof MatchBasisSchema>;

/**
 * Confidence thresholds. Server is the source of truth for the actual
 * score; these constants live client-side for UX (e.g. picking the right
 * status label) and audit-log consistency.
 */
export const MATCH_CONFIDENCE = {
  /** MRN-based match or MRN+DOB combo. */
  HIGH_MIN: 0.9,
  /** name+DOB combo. */
  LIKELY_MIN: 0.7,
  /** name-only — never auto-displays financial data. */
  UNSAFE_MAX: 0.5,
} as const;

/** Minimal Enter-side patient identity rendered in the sidebar. */
export const EnterPatientSchema = z.object({
  enterPatientId: z.string(),
  name: z.string(),
  /** ISO 8601 (YYYY-MM-DD). */
  dob: z.string().optional(),
  mrn: z.string().optional(),
});
export type EnterPatient = z.infer<typeof EnterPatientSchema>;

// ─── Real-Time Mode content — PRD §5 ──────────────────────────────────────

export const FinancialSummarySchema = z.object({
  outstandingPatientBalance: z.number().nullable(),
  outstandingInsuranceBalance: z.number().nullable().optional(),
  totalAr: z.number().nullable().optional(),
  /** ISO 8601 timestamp from the source-of-truth system. */
  lastUpdated: z.string().optional(),
});
export type FinancialSummary = z.infer<typeof FinancialSummarySchema>;

export const ClaimStatusSchema = z.enum([
  'paid',
  'denied',
  'submitted',
  'pending',
  'rejected',
  'partial',
  'unknown',
]);
export type ClaimStatus = z.infer<typeof ClaimStatusSchema>;

export const RecentClaimSchema = z.object({
  claimId: z.string(),
  /**
   * ISO 8601 (YYYY-MM-DD). Optional because Enter's
   * `ProviderPatientClaimStatement.fromDos` is optional upstream.
   */
  dateOfService: z.string().optional(),
  /**
   * Insurance payer name. Optional because Enter's claim-statement
   * object doesn't expose it directly — the resolver leaves it
   * undefined rather than fabricating a value.
   */
  payer: z.string().optional(),
  status: ClaimStatusSchema,
  cptCodes: z.array(z.string()).optional(),
  patientResponsibility: z.number().nullable().optional(),
  claimBalance: z.number().nullable().optional(),
});
export type RecentClaim = z.infer<typeof RecentClaimSchema>;

export const IssueTypeSchema = z.enum([
  'open_denial',
  'rejected_claim',
  'missing_documentation',
  'eligibility_issue',
  'patient_balance',
  'unposted_payment',
  'underpayment_suspected',
]);
export type IssueType = z.infer<typeof IssueTypeSchema>;

export const IssueSeveritySchema = z.enum(['high', 'medium', 'low']);
export type IssueSeverity = z.infer<typeof IssueSeveritySchema>;

export const CurrentIssueSchema = z.object({
  type: IssueTypeSchema,
  severity: IssueSeveritySchema,
  message: z.string(),
  claimId: z.string().optional(),
});
export type CurrentIssue = z.infer<typeof CurrentIssueSchema>;

export const RecommendedActionTypeSchema = z.enum([
  'review_open_denial',
  'explain_balance',
  'generate_account_summary',
  'create_account_note',
  'open_enter_account',
]);
export type RecommendedActionType = z.infer<typeof RecommendedActionTypeSchema>;

export const RecommendedActionSchema = z.object({
  type: RecommendedActionTypeSchema,
  label: z.string(),
  /** Optional deep-link into the Enter platform for this action. */
  href: z.string().optional(),
});
export type RecommendedAction = z.infer<typeof RecommendedActionSchema>;

/**
 * Discrepancy between Enter's authoritative balance and the EMR-visible
 * balance the bridge scraped. Surfaces a chip on the Balance card so
 * staff can investigate before discussing numbers with the patient.
 *
 * `deltaCents` is signed: positive means Enter > EMR (EMR is under-
 * counting), negative means Enter < EMR (EMR is over-counting).
 */
export const BalanceDiscrepancySchema = z.object({
  enterBalance: z.number(),
  emrBalance: z.number(),
  deltaCents: z.number().int(),
});
export type BalanceDiscrepancy = z.infer<typeof BalanceDiscrepancySchema>;

export const RealTimeModeDataSchema = z.object({
  patient: EnterPatientSchema,
  financialSummary: FinancialSummarySchema,
  recentClaims: z.array(RecentClaimSchema),
  currentIssues: z.array(CurrentIssueSchema),
  recommendedActions: z.array(RecommendedActionSchema),
  /** Deep-link into Enter platform for the matched patient. */
  enterPatientUrl: z.string().optional(),
  /**
   * Balance discrepancy vs the EMR-visible balance, when both Enter and
   * EMR balances are available AND differ beyond the rounding threshold.
   * Absent when no discrepancy is detected (vs. zeroed-out — keeps the
   * UI's "show chip?" check trivial).
   */
  discrepancy: BalanceDiscrepancySchema.optional(),
  /**
   * Patient A/R aging as rendered by the EMR, passed through from the observed
   * context. Enter's statement endpoint doesn't expose aging buckets, so this
   * is the ONLY source for the snapshot's graduated aging chips — absent for
   * EMRs that don't render one.
   */
  emrPatientAging: PatientAgingSchema.optional(),
});
export type RealTimeModeData = z.infer<typeof RealTimeModeDataSchema>;

// ─── The match state machine itself ───────────────────────────────────────

/**
 * Full discriminated union the sidebar consumes.
 *
 * Renderer rule (PRD §6, §10): only `matched` and `likely` may display
 * financial details. `unsafe` shows candidates only. `multiple` must
 * present a picker. `none` exposes a manual-search fallback.
 */
export const MatchStateSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('idle'),
  }),
  z.object({
    status: z.literal('loading'),
    context: ObservedContextSchema,
  }),
  z.object({
    status: z.literal('matched'),
    context: ObservedContextSchema,
    confidence: z.number(),
    basis: z.array(MatchBasisSchema),
    data: RealTimeModeDataSchema,
  }),
  z.object({
    status: z.literal('likely'),
    context: ObservedContextSchema,
    confidence: z.number(),
    basis: z.array(MatchBasisSchema),
    data: RealTimeModeDataSchema,
  }),
  z.object({
    status: z.literal('multiple'),
    context: ObservedContextSchema,
    candidates: z.array(EnterPatientSchema),
  }),
  z.object({
    status: z.literal('unsafe'),
    context: ObservedContextSchema,
    candidates: z.array(EnterPatientSchema),
  }),
  z.object({
    status: z.literal('none'),
    context: ObservedContextSchema,
  }),
  z.object({
    status: z.literal('error'),
    context: ObservedContextSchema.optional(),
    error: z.string(),
  }),
  /**
   * Calling user's organization has no connected Enter integration yet
   * (Vault has no `enter-health-integration-<orgId>-*` entry). The
   * sidebar renders a friendly "Connect Enter" CTA with a deep-link to
   * the Integrations settings page rather than an error toast.
   *
   * Distinct from `error` because it's not a failure — it's a
   * configuration gap the user can fix in two clicks.
   */
  z.object({
    status: z.literal('no_integration'),
    context: ObservedContextSchema,
  }),
]);
export type MatchState = z.infer<typeof MatchStateSchema>;

// ─── Real-Time Mode endpoint contract ─────────────────────────────────────

/**
 * Request body for `POST /real-time-mode/resolve`. Camel-case here on the
 * wire — translated to snake_case for the upstream Enter platform call by
 * the gateway if needed.
 *
 * `confirmedEnterPatientId` is set by the multi-match picker UI: when
 * the user disambiguates a `multiple` state by clicking a candidate,
 * the web app re-calls resolve with the chosen id so the gateway returns
 * a `matched` with the picked patient's data.
 */
export const ResolveRealTimeModeInputSchema = z.object({
  context: ObservedContextSchema,
  confirmedEnterPatientId: z.string().optional(),
  /**
   * When provided, the gateway only queries this specific Enter account
   * instead of fan-out searching all org accounts. Used when the user
   * has the "Search all accounts" toggle OFF — restricts RTM to whichever
   * account is currently selected in the chat toolbar.
   */
  restrictToAccountId: z.string().optional(),
});
export type ResolveRealTimeModeInput = z.infer<typeof ResolveRealTimeModeInputSchema>;

/**
 * Response schema. The discriminated `match` block carries everything the
 * sidebar needs; the gateway also stamps an audit-trail id for traceability.
 */
export const ResolveRealTimeModeOutputSchema = z.object({
  match: MatchStateSchema,
  /** Server-assigned id for this resolution — written to audit log. */
  resolutionId: z.string(),
  /**
   * The Enter account ID that produced the match. Only present when the
   * match status is `matched` or `likely`. The web app uses this to
   * auto-select the correct MCP account in the chat toolbar so the AI
   * can query the same practice that owns the matched patient.
   */
  matchedAccountId: z.string().optional(),
});
export type ResolveRealTimeModeOutput = z.infer<typeof ResolveRealTimeModeOutputSchema>;
